import React, { useState, useEffect, useCallback, useRef } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import {
  Button, Modal, Form, Input, Select, DatePicker, Space, Tag,
  Table, Tooltip, Spin, Alert, Divider, Switch,
  Row, Col, Popconfirm,
} from 'antd';
import CBInputNumber from '../../../components/CBInputNumber';
import CBModal from '../../../components/CBModal';
import SelectWithAdd from '../../../components/SelectWithAdd';
import {
  UserAddOutlined, PrinterOutlined,
  SaveOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
  FilePdfOutlined, CarOutlined, UserOutlined,
  EyeOutlined, ReloadOutlined, WarningOutlined,
  FileTextOutlined, CheckCircleOutlined, MedicineBoxOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import axiosInstance from '../../../variables/api/axiosInstance';
import { indexationEnCours, attendreIndexation } from '../../../services/gedIndexation';
import GedFileField from '../../../components/GedFileField';
import NoteMissionExpert from './NoteMissionExpert';
import NoteVictime from './NoteVictime';
import {
  chargerSignatureImprimable, confirmerImpressionSansSignature,
} from '../../../components/avertissementSignature';
import { printInIframe } from '../../../utils/printIframe';
import { TYPES_EXPERTISE, libelleTypeExpertise, libelleVictime } from '../../../variables/constants';
import { useTranslation, Trans } from 'react-i18next';
import i18n from '../../../i18n';
import { localeIntl } from '../../../i18n/format';
import { libellesTraduits } from '../../../i18n/libelles';
import { langueVictime } from '../../../i18n/documents';
//  Constantes 
const TYPE_EXPERTISE = TYPES_EXPERTISE;
const PRETIUM = [
  'NEANT', 'TRES_LEGER', 'LEGER', 'MODERE', 'MOYEN',
  'ASSEZ_IMPORTANT', 'IMPORTANT', 'TRES_IMPORTANT', 'EXCEPTIONNEL',
];
const STATUT_COLORS = { EN_ATTENTE: 'orange', RAPPORT_RECU: 'blue', CLOTURE: 'green' };
// Libellés affichés des codes : en français, le code sans ses « _ », comme avant.
const PRETIUM_LIBELLE = libellesTraduits('dossierSinistre:ongletExpertises.pretium');
const STATUT_LIBELLE = libellesTraduits('dossierSinistre:ongletExpertises.statutsAffectation');
const libellePretium = (p) => (p ? PRETIUM_LIBELLE[p] || p.replace(/_/g, ' ') : p);
const G = '#1a6b3c';
const disabledFuture = (current) => current && current > dayjs().endOf('day');

//  Composant principal 
export default function OngletExpertises({ sinistre, victimes }) {
  const { t } = useTranslation('dossierSinistre');
  /* TE : l'expertise est réalisée dans le pays de l'accident, par le bureau
     homologue. On n'affecte pas d'expert ici - on ENREGISTRE le rapport reçu,
     avec le nom de son expert en saisie libre. */
  const estTe = sinistre?.typeSinistre === 'SURVENU_ETRANGER';
  const [modalChoixTe, setModalChoixTe] = useState(null); // null | { victimeTrackingId, nature }
  const [affectations, setAffectations] = useState([]);
  const [expertsMed, setExpertsMed] = useState([]);
  const [expertsAuto, setExpertsAuto] = useState([]);
  // Maps victimeTrackingId → liste d'expertises
  const [rapportsMed, setRapportsMed] = useState({});
  const [rapportsMat, setRapportsMat] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  //  États des modals
  const [modalAff, setModalAff] = useState(false);
  // Victime sélectionnée dans le modal d'affectation : détermine le type d'expertise
  // (corporel → médicale, matériel → automobile, mixte → les deux) et filtre la liste d'experts.
  const [victimeAff, setVictimeAff] = useState(null);

  // Modal rapport médical - création OU édition
  // { victimeTrackingId, affectation, expertiseExistante | null }
  const [modalRapportMed, setModalRapportMed] = useState(null);

  // Modal rapport matériel - création OU édition
  // { victimeTrackingId, affectation, expertiseExistante | null }
  const [modalRapportMat, setModalRapportMat] = useState(null);

  // Modal visualisation complète (métadonnées + PDF) d'un rapport existant
  // { expertise, type: 'med' | 'mat' }
  const [modalVoirRapport, setModalVoirRapport] = useState(null);

  // Modal aperçu courrier HTML (backend ou local)
  const [modalCourrier, setModalCourrier] = useState(null); // { html, titre, css? }

  // Modal aperçu Note de Mission (générée localement)
  const [modalNoteMission, setModalNoteMission] = useState(null); // affectation | null

  // Modal aperçu Note à la Victime (générée localement)
  const [modalNoteVictime, setModalNoteVictime] = useState(null); // affectation | null

  // Refs pour capturer le contenu des modals (export PDF/Word)
  const noteMissionRef = useRef(null);
  const noteVictimeRef = useRef(null);

  // Fichiers PDF sélectionnés via GedFileField (null = pas de nouveau fichier)
  const [fichierMed, setFichierMed] = useState(null);
  const [fichierMat, setFichierMat] = useState(null);
  /* Le rapport est déjà archivé : le champ d'upload reste masqué tant que le
     rédacteur ne demande pas explicitement à remplacer le document.

     POURQUOI LE MASQUER. Il s'affichait avec la mention « laisser vide pour
     conserver le document existant » - une consigne qu'on lit APRÈS avoir
     cherché quel fichier déposer. Un champ visible demande à être rempli ; il
     faisait douter le rédacteur de ce qui était déjà là.

     POURQUOI GARDER UNE PORTE. Sans elle, un PDF erroné n'aurait plus qu'une
     issue : supprimer le rapport et le ressaisir. La demande était de ne plus
     AFFICHER le champ, pas de rendre la correction impossible. */
  const [remplacerMed, setRemplacerMed] = useState(false);
  const [remplacerMat, setRemplacerMat] = useState(false);

  // PDF pour le modal voir-rapport (blob URL)
  const [blobUrl, setBlobUrl] = useState(null);
  const [loadingBlob, setLoadingBlob] = useState(false);

  const [formAff] = Form.useForm();
  const [formMed] = Form.useForm();
  const [formMat] = Form.useForm();

  //  Chargement des données 
  const charger = useCallback(async () => {
    if (!sinistre?.sinistreTrackingId) return;
    setLoading(true);
    try {
      const [affR, medR, autoR] = await Promise.all([
        axiosInstance.get(`/v1/affectations-expert/sinistre/${sinistre.sinistreTrackingId}`),
        axiosInstance.get('/v1/experts/type/MEDICAL'),
        axiosInstance.get('/v1/experts/type/AUTOMOBILE'),
      ]);
      const aff = affR.data?.data || [];
      setAffectations(aff);
      setExpertsMed(medR.data?.data || []);
      setExpertsAuto(autoR.data?.data || []);

      // Charger les expertises par victime pour afficher les boutons Voir/Modifier.
      // Toutes les victimes du dossier : un TE n'a pas d'affectation, ses expertises
      // (enregistrées depuis l'homologue) doivent quand même se voir.
      const victimeIds = [...new Set([
        ...aff.map(a => a.victimeTrackingId),
        ...(victimes || []).map(v => v.victimeTrackingId),
      ].filter(Boolean))];
      const [medResults, matResults] = await Promise.all([
        Promise.all(victimeIds.map(id =>
          axiosInstance.get(`/v1/expertises-medicales/victime/${id}`)
            .then(r => ({ id, data: r.data?.data || [] }))
            .catch(() => ({ id, data: [] }))
        )),
        Promise.all(victimeIds.map(id =>
          axiosInstance.get(`/v1/expertises-materielles/victime/${id}`)
            .then(r => ({ id, data: r.data?.data || [] }))
            .catch(() => ({ id, data: [] }))
        )),
      ]);
      const medMap = {};
      medResults.forEach(({ id, data }) => { medMap[id] = data; });
      const matMap = {};
      matResults.forEach(({ id, data }) => { matMap[id] = data; });
      setRapportsMed(medMap);
      setRapportsMat(matMap);
    } catch (e) {
      /* LE MESSAGE DU SERVEUR, ET NON UN MESSAGE A NOUS.

         Ce `catch` etait vide - `catch {` sans parametre - et remplacait toute cause par
         « Erreur chargement expertises ». Le statut HTTP, le message du serveur et sa
         reference d'incident etaient jetes : il ne restait, pour chercher, que le nom de
         l'onglet. Trois appels partent d'ici, et rien ne disait lequel avait echoue.

         L'ecran « Expertises commises » affiche deja `data.message` ; celui-ci ne le
         faisait pas. */
      const detail = e?.response?.data?.message
        || (e?.response?.status ? i18n.t('dossierSinistre:ongletExpertises.serveurARepondu', { statut: e.response.status }) : e?.message);
      toast.error(detail
        ? i18n.t('dossierSinistre:ongletExpertises.chargementImpossibleDetail', { detail })
        : i18n.t('dossierSinistre:ongletExpertises.chargementImpossible'));
      // Et la cause complete dans la console, pour le cas ou le message ne suffit pas.
      console.error('Chargement des expertises', e);
    }
    setLoading(false);
  }, [sinistre?.sinistreTrackingId, victimes]);

  useEffect(() => { charger(); }, [charger]);

  /**
   * Suppression logique d'un rapport (le document GED, lui, reste dans le
   * dossier de la victime). Le fil de maturité est recalculé côté serveur.
   */
  const supprimerRapport = async (type, rapport) => {
    try {
      if (type === 'med') {
        await axiosInstance.delete(`/v1/expertises-medicales/${rapport.expertiseMedTrackingId}`);
      } else {
        await axiosInstance.delete(`/v1/expertises-materielles/${rapport.expertiseMaTrackingId}`);
      }
      toast.success(t('ongletExpertises.rapportSupprime'));
      charger();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('ongletExpertises.suppressionImpossible'));
    }
  };

  // Délai par défaut de déroulement d'une expertise (paramètre ET_EXPERTISE_RAPPORT).
  // Fallback : 20 jours calendaires si le paramètre n'est pas retourné (il peut être inactif).
  const [delaiExpertise, setDelaiExpertise] = useState({ valeur: 20, unite: 'JOURS_CALENDAIRES' });
  useEffect(() => {
    axiosInstance.get('/v1/delais/parametres')
      .then(r => {
        const p = (r.data?.data || []).find(x => x.codeDelai === 'ET_EXPERTISE_RAPPORT');
        if (p) setDelaiExpertise({ valeur: Number(p.valeur), unite: p.unite });
      })
      .catch(() => { /* on garde le fallback */ });
  }, []);

  const ajouterDelaiExpertise = (date) => {
    if (!date) return null;
    const { valeur, unite } = delaiExpertise;
    const unit = unite === 'MOIS' ? 'month' : unite === 'ANS' ? 'year' : 'day';
    return date.add(valeur, unit);
  };

  // À l'ouverture du modal d'affectation : date d'affectation = aujourd'hui,
  // date limite rapport = aujourd'hui + délai ET_EXPERTISE_RAPPORT.
  useEffect(() => {
    if (modalAff) {
      const dAff = dayjs();
      formAff.setFieldsValue({ dateAffectation: dAff, dateLimiteRapport: ajouterDelaiExpertise(dAff) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalAff, delaiExpertise]);

  // Libère le blob URL quand on ferme le modal voir-rapport
  useEffect(() => {
    if (!modalVoirRapport && blobUrl) {
      URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
    }
  }, [modalVoirRapport]);

  //  Reset modals 
  const closeMed = () => {
    setModalRapportMed(null);
    setFichierMed(null);
    // Sans cette remise a zero, le rapport suivant s'ouvrirait avec le champ
    // deja deplie, alors que son document est archive lui aussi.
    setRemplacerMed(false);
    formMed.resetFields();
  };

  const closeMat = () => {
    setModalRapportMat(null);
    setFichierMat(null);
    // Sans cette remise a zero, le rapport suivant s'ouvrirait avec le champ
    // deja deplie, alors que son document est archive lui aussi.
    setRemplacerMat(false);
    formMat.resetFields();
  };

  //  Upload vers GED 
  /**
   * Envoie le fichier PDF à /v1/ged/documents et retourne l'ossanGedDocumentId.
   *
   * @param fichier       - Fichier PDF sélectionné via GedFileField
   * @param titre         - Titre du document dans la GED
   * @param typeDocument  - Tag GED (ex: RAPPORT_MEDICAL, RAPPORT_MATERIEL)
   * @param victimeTrackingId - UUID de la victime pour stocker le doc dans son sous-dossier GED
   *
   * Corrections appliquées :
   *  1. victimeTrackingId passé dans la metadata → le doc est classé dans le dossier victime
   *  2. Lecture du bon champ : ossanGedDocumentId (et non documentId)
   *  3. Polling si Paperless traite l'OCR en asynchrone (ossanGedDocumentId null à l'upload)
   */

  const uploadVersGed = async (fichier, titre, typeDocument, victimeTrackingId) => {
    if (!fichier) return null;
    try {
      const fd = new FormData();
      fd.append('file', new Blob([fichier], { type: 'application/pdf' }), fichier.name);
      fd.append(
        'metadata',
        new Blob(
          [JSON.stringify({
            titre,
            typeDocument,
            sinistreTrackingId: sinistre.sinistreTrackingId,
            // FIX 1 : victimeTrackingId → le backend classe le doc dans le sous-dossier de la victime
            victimeTrackingId: victimeTrackingId ?? null,
          })],
          { type: 'application/json' },
        ),
      );
      const r = await axiosInstance.post('/v1/ged/documents', fd);
      const doc = r.data?.data;

      // FIX 2 : le champ s'appelle ossanGedDocumentId, pas documentId
      if (doc?.ossanGedDocumentId) return doc.ossanGedDocumentId;

      // FIX 3 : Paperless indexe parfois de façon asynchrone (OCR) → sondage
      if (indexationEnCours(doc)) {
        toast.info(t('ongletExpertises.indexationEnCours'));
        const resolvedId = await attendreIndexation(doc.ossanGedDocumentTrackingId);
        if (resolvedId) return resolvedId;
        toast.warning(t('ongletExpertises.ocrEnCours'));
        return null;
      }

      return null;
    } catch (e) {
      // POURQUOI l'archivage a echoue : l'expertise est bien enregistree, et c'est la
      // seule chose que ce message disait. Reprendre l'archivage demande d'en savoir plus.
      const detail = e?.response?.data?.message || e?.message;
      toast.warning(detail
        ? t('ongletExpertises.archivageEchecDetail', { detail })
        : t('ongletExpertises.archivageEchec'));
      console.error('Archivage GED de l expertise', e);
      return null;
    }
  };

  //  Ouverture modal médical (création ou édition) 
  const ouvrirModalMed = (affectation) => {
    setFichierMed(null);
    const existants = rapportsMed[affectation.victimeTrackingId] || [];
    const expertiseExistante =
      existants.find(e => e.typeExpertise === affectation.typeExpertise) || existants[0] || null;
    setModalRapportMed({ victimeTrackingId: affectation.victimeTrackingId, affectation, expertiseExistante });

    if (expertiseExistante) {
      formMed.setFieldsValue({
        typeExpertise: expertiseExistante.typeExpertise,
        expertTrackingId: affectation.expertTrackingId,
        dateDemande: expertiseExistante.dateDemande ? dayjs(expertiseExistante.dateDemande) : null,
        dateRapport: expertiseExistante.dateRapport ? dayjs(expertiseExistante.dateRapport) : null,
        dateConsolidation: expertiseExistante.dateConsolidation ? dayjs(expertiseExistante.dateConsolidation) : null,
        tauxIpp: expertiseExistante.tauxIpp,
        dureeIttJours: expertiseExistante.dureeIttJours,
        // dureeItpJours: expertiseExistante.dureeItpJours,
        pretiumDoloris: expertiseExistante.pretiumDoloris,
        prejudiceEsthetique: expertiseExistante.prejudiceEsthetique,
        necessiteTiercePersonne: expertiseExistante.necessiteTiercePersonne,
        honoraires: expertiseExistante.honoraires,
        honorairesContreExpertise: expertiseExistante.honorairesContreExpertise,
        nomExpertExterne: expertiseExistante.expertNomComplet,
      });
    } else {
      formMed.setFieldsValue({
        typeExpertise: affectation.typeExpertise,
        expertTrackingId: affectation.expertTrackingId,
      });
    }
  };

  //  Ouverture modal matériel (création ou édition) 
  const ouvrirModalMat = (affectation) => {
    setFichierMat(null);
    const existants = rapportsMat[affectation.victimeTrackingId] || [];
    const expertiseExistante =
      existants.find(e => e.typeExpertise === affectation.typeExpertise) || existants[0] || null;
    setModalRapportMat({ victimeTrackingId: affectation.victimeTrackingId, affectation, expertiseExistante });

    if (expertiseExistante) {
      formMat.setFieldsValue({
        typeExpertise: expertiseExistante.typeExpertise,
        expertTrackingId: affectation.expertTrackingId,
        dateDemande: expertiseExistante.dateDemande ? dayjs(expertiseExistante.dateDemande) : null,
        dateRapport: expertiseExistante.dateRapport ? dayjs(expertiseExistante.dateRapport) : null,
        marqueVehicule: expertiseExistante.marqueVehicule,
        modeleVehicule: expertiseExistante.modeleVehicule,
        immatriculation: expertiseExistante.immatriculation,
        anneeVehicule: expertiseExistante.anneeVehicule,
        natureDommages: expertiseExistante.natureDommages,
        estVei: expertiseExistante.estVei,
        valeurVehiculeNeuf: expertiseExistante.valeurVehiculeNeuf,
        valeurVenal: expertiseExistante.valeurVenal,
        valeurReparable: expertiseExistante.valeurReparable,
        montantDevis: expertiseExistante.montantDevis,
        montantDitExpert: expertiseExistante.montantDitExpert,
        honoraires: expertiseExistante.honoraires,
        observations: expertiseExistante.observations,
      });
    } else {
      formMat.setFieldsValue({
        typeExpertise: affectation.typeExpertise,
        expertTrackingId: affectation.expertTrackingId,
      });
    }
  };

  //  Affectation expert 
  const affecterExpert = async (values) => {
    setSaving(true);
    try {
      await axiosInstance.post('/v1/affectations-expert', {
        ...values,
        sinistreTrackingId: sinistre.sinistreTrackingId,
        dateAffectation: values.dateAffectation?.format('YYYY-MM-DD'),
        dateLimiteRapport: values.dateLimiteRapport?.format('YYYY-MM-DD'),
      });
      toast.success(t('ongletExpertises.expertAffecte'));
      setModalAff(false);
      charger();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('ongletExpertises.erreurAffectation'));
    }
    setSaving(false);
  };

  //  Enregistrement / mise à jour rapport médical 
  const enregistrerRapportMed = async (values) => {
    setSaving(true);
    const { expertiseExistante, victimeTrackingId } = modalRapportMed;
    try {
      // Upload du nouveau PDF si sélectionné, sinon conserver l'existant
      let ossanGedDocumentId = expertiseExistante?.ossanGedDocumentId ?? null;
      if (fichierMed) {
        const newId = await uploadVersGed(
          fichierMed,
          `Rapport médical - ${sinistre.numeroSinistreLocal || sinistre.sinistreTrackingId}`,
          'RAPPORT_MEDICAL',
          victimeTrackingId,   // ← classe le doc dans le sous-dossier GED de la victime
        );
        if (newId) ossanGedDocumentId = newId;
      }

      const payload = {
        ...values,
        victimeTrackingId,
        dateDemande: values.dateDemande?.format('YYYY-MM-DD'),
        dateRapport: values.dateRapport?.format('YYYY-MM-DD'),
        dateConsolidation: values.dateConsolidation?.format('YYYY-MM-DD'),
        ossanGedDocumentId,
      };

      if (expertiseExistante?.expertiseMedTrackingId) {
        await axiosInstance.put(`/v1/expertises-medicales/${expertiseExistante.expertiseMedTrackingId}`, payload);
        toast.success(t('ongletExpertises.medMisAJour'));
      } else {
        await axiosInstance.post('/v1/expertises-medicales', payload);
        toast.success(t('ongletExpertises.medEnregistre'));
      }

      // Passer l'affectation en RAPPORT_RECU si elle est encore EN_ATTENTE
      const aff = modalRapportMed.affectation;
      if (aff?.affectationTrackingId && aff.statut === 'EN_ATTENTE') {
        await axiosInstance.patch(
          `/v1/affectations-expert/${aff.affectationTrackingId}/statut`,
          null,
          { params: { statut: 'RAPPORT_RECU' } },
        ).catch(() => { });
      }

      closeMed();
      charger();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('ongletExpertises.erreurEnregistrement'));
    }
    setSaving(false);
  };

  //  Enregistrement / mise à jour rapport matériel 
  const enregistrerRapportMat = async (values) => {
    setSaving(true);
    const { expertiseExistante, victimeTrackingId } = modalRapportMat;
    try {
      let ossanGedDocumentId = expertiseExistante?.ossanGedDocumentId ?? null;
      if (fichierMat) {
        const newId = await uploadVersGed(
          fichierMat,
          `Rapport matériel - ${sinistre.numeroSinistreLocal || sinistre.sinistreTrackingId}`,
          'RAPPORT_MATERIEL',
          victimeTrackingId,   // ← classe le doc dans le sous-dossier GED de la victime
        );
        if (newId) ossanGedDocumentId = newId;
      }

      const payload = {
        ...values,
        victimeTrackingId,
        sinistreTrackingId: sinistre.sinistreTrackingId,
        dateDemande: values.dateDemande?.format('YYYY-MM-DD'),
        dateRapport: values.dateRapport?.format('YYYY-MM-DD'),
        ossanGedDocumentId,
      };

      if (expertiseExistante?.expertiseMaTrackingId) {
        await axiosInstance.put(`/v1/expertises-materielles/${expertiseExistante.expertiseMaTrackingId}`, payload);
        toast.success(t('ongletExpertises.matMisAJour'));
      } else {
        await axiosInstance.post('/v1/expertises-materielles', payload);
        toast.success(t('ongletExpertises.matEnregistre'));
      }

      // Passer l'affectation en RAPPORT_RECU si elle est encore EN_ATTENTE
      const aff = modalRapportMat.affectation;
      if (aff?.affectationTrackingId && aff.statut === 'EN_ATTENTE') {
        await axiosInstance.patch(
          `/v1/affectations-expert/${aff.affectationTrackingId}/statut`,
          null,
          { params: { statut: 'RAPPORT_RECU' } },
        ).catch(() => { });
      }

      closeMat();
      charger();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('ongletExpertises.erreurEnregistrement'));
    }
    setSaving(false);
  };

  //  Prévisualisation PDF depuis la GED 
  const previsualiserDocument = async (ossanGedDocumentId) => {
    if (!ossanGedDocumentId) return;
    setLoadingBlob(true);
    try {
      const res = await axiosInstance.get(
        `/v1/ged/documents/${ossanGedDocumentId}/telecharger`,
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      setBlobUrl(URL.createObjectURL(blob));
    } catch (e) {
      // Un PDF absent de la GED et un jeton expire ne se corrigent pas de la meme facon :
      // le message du serveur les distingue, « Impossible de charger » non.
      toast.error(e?.response?.data?.message || t('ongletExpertises.pdfImpossible'));
      console.error('Chargement du PDF d expertise', e);
    }
    setLoadingBlob(false);
  };

  // Ouvre le modal voir-rapport et lance la prévisualisation du PDF
  const ouvrirVoirRapport = async (expertise, type) => {
    setBlobUrl(null);
    setModalVoirRapport({ expertise, type });
    if (expertise?.ossanGedDocumentId) {
      await previsualiserDocument(expertise.ossanGedDocumentId);
    }
  };

  const fermerVoirRapport = () => {
    setModalVoirRapport(null); // l'useEffect se charge de révoquer le blob
  };

  //  Courriers 
  const fetchEtImprimerCourrier = async (trackingId, titre) => {
    if (!trackingId) { toast.warning(t('ongletExpertises.courrierNonTrouve')); return; }
    try {
      const r = await axiosInstance.get(`/v1/courriers/${trackingId}`);
      setModalCourrier({ html: r.data?.data?.corpsHtml || '<p>Courrier non disponible</p>', titre });
    } catch (e) {
      // Meme raison qu'au chargement des expertises : la cause ne doit pas disparaitre.
      toast.error(e?.response?.data?.message || t('ongletExpertises.courrierImpossible'));
      console.error('Chargement du courrier', e);
    }
  };

  /* Les deux notes sont dimensionnees en millimetres et occupent la zone utile de
     la page sans etre remises a l'echelle. Les marges vivent ici, dans @page, et
     non dans un padding du document : c'est ce qui permet aux tailles declarees en
     points d'etre celles qui s'impriment. L'ancienne feuille appliquait un
     zoom: 0.78 qui reduisait tout de 22 %. */
  const NOTE_A4_PRINT_CSS = `
    @page { size: A4; margin: 18mm 20mm; }
    html, body { margin: 0; padding: 0; background: #fff; }
    * { box-sizing: border-box; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  `;

  const doPrint = () => {
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head>
      <title>${modalCourrier.titre}</title>
      <style>body{font-family:Arial,sans-serif;margin:40px}</style>
    </head><body>${modalCourrier.html}</body></html>`);
    w.document.close();
    w.print();
  };

  /* La signature est chargee AVANT le rendu : renderToStaticMarkup serialise le
     resultat en chaine, et aucun effet React ne s'y execute - BlocSignature ne
     chargeait donc jamais rien, et ces notes sortaient sans signature quel que soit
     le parametrage. Et l'on avertit avant le tirage : les signatures expirent. */
  const imprimerNoteMission = (aff) => confirmerImpressionSansSignature(
    'NOTE_MISSION_EXPERT',
    async () => {
      const signature = await chargerSignatureImprimable(
        'NOTE_MISSION_EXPERT', sinistre?.sinistreTrackingId);
      const html = renderToStaticMarkup(
        <NoteMissionExpert sinistre={sinistre} affectation={aff} signature={signature} />
      );
      printInIframe(html, NOTE_A4_PRINT_CSS, 'Note de mission');
    },
  );

  // Immatriculation de l'engin de la victime (portée par la fiche victime, pas par l'affectation).
  const immatDe = (aff) => (victimes || []).find(v => v.victimeTrackingId === aff?.victimeTrackingId)?.immatriculation || null;

  // Langue de la note : celle de la victime (fiche), pas celle de l'écran.
  const langueDe = (aff) => langueVictime((victimes || []).find(v => v.victimeTrackingId === aff?.victimeTrackingId));

  const imprimerNoteVictime = (aff) => confirmerImpressionSansSignature(
    'NOTE_VICTIME',
    async () => {
      const signature = await chargerSignatureImprimable(
        'NOTE_VICTIME', sinistre?.sinistreTrackingId);
      const html = renderToStaticMarkup(
        <NoteVictime
          sinistre={sinistre}
          affectation={aff}
          immatriculation={immatDe(aff)}
          signature={signature}
        langue={langueDe(aff)}
        />
      );
      printInIframe(html, NOTE_A4_PRINT_CSS, 'Note de mission');
    },
  );

  //  Colonnes du tableau 
  const colAff = [
    {
      title: t('ongletExpertises.colonnes.victime'), key: 'victime',
      render: (_, r) => <span style={{ fontWeight: 600 }}>{r.victimeNomPrenom}</span>,
    },
    {
      title: t('ongletExpertises.colonnes.expert'), key: 'expert',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <span>{r.expertNomComplet}</span>
          <span style={{ fontSize: 11, color: '#94a3b8' }}>
            {r.expertTypeLabel === 'MEDICAL'
              ? <><MedicineBoxOutlined style={{ marginRight: 4 }} />{t('ongletExpertises.medical')}</>
              : <><CarOutlined style={{ marginRight: 4 }} />{t('ongletExpertises.automobile')}</>}
          </span>
        </Space>
      ),
    },
    { title: t('ongletExpertises.colonnes.type'), dataIndex: 'typeExpertise', render: v => <Tag>{libelleTypeExpertise(v)}</Tag> },
    { title: t('ongletExpertises.colonnes.affectation'), dataIndex: 'dateAffectation', render: v => v || '-' },
    {
      title: t('ongletExpertises.colonnes.dateLimite'), dataIndex: 'dateLimiteRapport',
      render: v => {
        if (!v) return '-';
        const retard = new Date(v) < new Date();
        return (
          <span style={{ color: retard ? '#dc2626' : 'inherit', fontWeight: retard ? 600 : 400 }}>
            {retard && <WarningOutlined style={{ marginRight: 4 }} />}{v}
          </span>
        );
      },
    },
    {
      title: t('ongletExpertises.colonnes.statut'), dataIndex: 'statut',
      render: v => <Tag color={STATUT_COLORS[v] || 'default'}>{STATUT_LIBELLE[v] || v?.replace(/_/g, ' ')}</Tag>,
    },
    {
      title: t('ongletExpertises.colonnes.courriers'), key: 'courriers',
      render: (_, r) => (
        <Space wrap>
          <Tooltip title={t('ongletExpertises.apercuNoteMission')}>
            <Button size="small" icon={<EyeOutlined />}
              style={{ borderColor: G, color: G }}
              onClick={() => setModalNoteMission(r)}>
              {t('ongletExpertises.noteMission')}
            </Button>
          </Tooltip>
          {(r.expertTypeLabel === 'MEDICAL' || r.expertTypeLabel === 'AUTOMOBILE') && (
            <Tooltip title={r.expertTypeLabel === 'AUTOMOBILE'
              ? t('ongletExpertises.noteVictimeAuto')
              : t('ongletExpertises.noteVictimeMed')}>
              <Button size="small" icon={<EyeOutlined />}
                style={{ borderColor: '#2563eb', color: '#2563eb' }}
                onClick={() => setModalNoteVictime(r)}>
                {t('ongletExpertises.noteVictime')}
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: t('ongletExpertises.colonnes.rapport'), key: 'rapport', width: 240,
      render: (_, r) => {
        const isMed = r.expertTypeLabel === 'MEDICAL';
        const isMat = r.expertTypeLabel === 'AUTOMOBILE';
        const rapMed = rapportsMed[r.victimeTrackingId] || [];
        const rapMat = rapportsMat[r.victimeTrackingId] || [];
        const aRapMed = rapMed.length > 0;
        const aRapMat = rapMat.length > 0;
        // Rapport le plus pertinent pour cette affectation
        const rapMedP = rapMed.find(e => e.typeExpertise === r.typeExpertise) || rapMed[0];
        const rapMatP = rapMat.find(e => e.typeExpertise === r.typeExpertise) || rapMat[0];

        return (
          <Space wrap>
            {isMed && (
              <>
                {/* Bouton Voir : métadonnées + PDF */}
                {aRapMed && (
                  <Tooltip title={t('ongletExpertises.voirRapport')}>
                    <Button
                      size="small"
                      icon={<EyeOutlined />}
                      style={{ borderColor: '#3b82f6', color: '#3b82f6' }}
                      onClick={() => ouvrirVoirRapport(rapMedP, 'med')}
                    >
                      {t('ongletExpertises.voir')}
                    </Button>
                  </Tooltip>
                )}
                {/* Bouton Modifier */}
                {aRapMed ? (
                  <>
                    <Tooltip title={t('ongletExpertises.modifierMed')}>
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        style={{ borderColor: '#f97316', color: '#f97316' }}
                        onClick={() => ouvrirModalMed(r)}
                      >
                        {t('ongletExpertises.modifier')}
                      </Button>
                    </Tooltip>
                    <Popconfirm title={t('ongletExpertises.supprimerMed')} okText={t('ongletExpertises.supprimer')} cancelText={t('ongletExpertises.annuler')}
                      okButtonProps={{ danger: true }} onConfirm={() => supprimerRapport('med', rapMedP)}>
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </>
                ) : (
                  <Tooltip title={t('ongletExpertises.enregistrerMed')}>
                    <Button
                      size="small" type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => ouvrirModalMed(r)}
                      style={{ background: G, borderColor: G }}
                    >
                      {t('ongletExpertises.rapportMedCourt')}
                    </Button>
                  </Tooltip>
                )}
              </>
            )}

            {isMat && (
              <>
                {/* Bouton Voir */}
                {aRapMat && (
                  <Tooltip title={t('ongletExpertises.voirRapport')}>
                    <Button
                      size="small"
                      icon={<EyeOutlined />}
                      style={{ borderColor: '#3b82f6', color: '#3b82f6' }}
                      onClick={() => ouvrirVoirRapport(rapMatP, 'mat')}
                    >
                      {t('ongletExpertises.voir')}
                    </Button>
                  </Tooltip>
                )}
                {/* Bouton Modifier */}
                {aRapMat ? (
                  <>
                    <Tooltip title={t('ongletExpertises.modifierMat')}>
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        style={{ borderColor: '#f97316', color: '#f97316' }}
                        onClick={() => ouvrirModalMat(r)}
                      >
                        {t('ongletExpertises.modifier')}
                      </Button>
                    </Tooltip>
                    <Popconfirm title={t('ongletExpertises.supprimerMat')} okText={t('ongletExpertises.supprimer')} cancelText={t('ongletExpertises.annuler')}
                      okButtonProps={{ danger: true }} onConfirm={() => supprimerRapport('mat', rapMatP)}>
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </>
                ) : (
                  <Tooltip title={t('ongletExpertises.enregistrerMat')}>
                    <Button
                      size="small" type="primary"
                      icon={<CarOutlined />}
                      onClick={() => ouvrirModalMat(r)}
                      style={{ background: G, borderColor: G }}
                    >
                      {t('ongletExpertises.rapportMatCourt')}
                    </Button>
                  </Tooltip>
                )}
              </>
            )}
          </Space>
        );
      },
    },
  ];

  if (loading) return <Spin style={{ display: 'block', padding: 40 }} />;

  //  Rendu 
  return (
    <div style={{ padding: '16px 0' }}>

      {/* En-tête */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>
          {estTe ? t('ongletExpertises.titreTe') : t('ongletExpertises.titre')}
        </h3>
        <Space>
          <Button icon={<ReloadOutlined />} size="small" onClick={charger}>{t('ongletExpertises.actualiser')}</Button>
          {estTe ? (
            <Button type="primary" icon={<FileTextOutlined />} onClick={() => setModalChoixTe({})}
              style={{ background: G, borderColor: G }}>
              {t('ongletExpertises.enregistrerExpertise')}
            </Button>
          ) : (
            <Button type="primary" icon={<UserAddOutlined />} onClick={() => setModalAff(true)}
              style={{ background: G, borderColor: G }}>
              {t('ongletExpertises.affecterExpert')}
            </Button>
          )}
        </Space>
      </div>

      {estTe && (
        <Alert type="warning" showIcon style={{ marginBottom: 16, borderRadius: 8 }}
          message={t('ongletExpertises.teMessage')}
          description={t('ongletExpertises.teDescription')} />
      )}

      {estTe && (() => {
        const nomDe = (id) => {
          const v = (victimes || []).find(x => x.victimeTrackingId === id);
          return v ? `${v.prenoms || ''} ${v.nom || ''}`.trim() : '-';
        };
        const lignes = [
          ...Object.entries(rapportsMed).flatMap(([vid, l]) => l.map(e => ({
            cle: `med-${e.expertiseMedTrackingId}`, vid, nature: t('ongletExpertises.natureMed'), e, type: 'med',
          }))),
          ...Object.entries(rapportsMat).flatMap(([vid, l]) => l.map(e => ({
            cle: `mat-${e.expertiseMaTrackingId}`, vid, nature: t('ongletExpertises.natureMat'), e, type: 'mat',
          }))),
        ];
        if (!lignes.length) {
          return <Alert type="info" showIcon style={{ marginBottom: 16 }}
            message={t('ongletExpertises.teAucune')} />;
        }
        return (
          <Table size="small" pagination={false} style={{ marginBottom: 24 }} rowKey="cle"
            dataSource={lignes}
            columns={[
              { title: t('ongletExpertises.colonnes.victime'), key: 'v', render: (_, r) => nomDe(r.vid) },
              { title: t('ongletExpertises.colonnes.nature'), dataIndex: 'nature', key: 'n', width: 90 },
              { title: t('ongletExpertises.colonnes.type'), key: 't', width: 140, render: (_, r) => libelleTypeExpertise(r.e.typeExpertise) || r.e.typeExpertise || '-' },
              { title: t('ongletExpertises.colonnes.expertHomologue'), key: 'x', render: (_, r) => r.e.expertNomComplet || '-' },
              { title: t('ongletExpertises.colonnes.rapport'), key: 'd', width: 100, render: (_, r) => (r.e.dateRapport ? dayjs(r.e.dateRapport).format('DD/MM/YYYY') : '-') },
              { title: t('ongletExpertises.colonnes.consolidation'), key: 'c', width: 110, render: (_, r) => (r.type === 'med' ? (r.e.dateConsolidation ? dayjs(r.e.dateConsolidation).format('DD/MM/YYYY') : <Tag color="orange" style={{ borderRadius: 20 }}>{t('ongletExpertises.attendue')}</Tag>) : '-') },
              {
                title: '', key: 'a', width: 150, render: (_, r) => (
                  <Space size={4}>
                    {r.e.ossanGedDocumentId && (
                      <Button size="small" icon={<EyeOutlined />} onClick={() => ouvrirVoirRapport(r.e, r.type)} />
                    )}
                    <Button size="small" icon={<EditOutlined />} onClick={() => {
                      const pseudoAff = { victimeTrackingId: r.vid, victimeNomPrenom: nomDe(r.vid), typeExpertise: r.e.typeExpertise };
                      if (r.type === 'med') ouvrirModalMed(pseudoAff); else ouvrirModalMat(pseudoAff);
                    }} />
                    <Popconfirm title={t('ongletExpertises.supprimerExpertise')} okText={t('ongletExpertises.supprimer')} cancelText={t('ongletExpertises.annuler')}
                      okButtonProps={{ danger: true }} onConfirm={() => supprimerRapport(r.type, r.e)}>
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                )
              },
            ]} />
        );
      })()}

      {affectations.length === 0
        ? (estTe
          ? null
          : <Alert type="info" message={t('ongletExpertises.aucuneAffectation')} showIcon style={{ marginBottom: 16 }} />)
        : <Table dataSource={affectations} columns={colAff} rowKey="affectationTrackingId"
          size="small" pagination={false} style={{ marginBottom: 24 }} />
      }

      {/* TE : choisir la victime et la nature, puis le formulaire de rapport habituel */}
      <Modal open={!!modalChoixTe} title={t('ongletExpertises.choixTeTitre')}
        onCancel={() => setModalChoixTe(null)} okText={t('ongletExpertises.continuer')} cancelText={t('ongletExpertises.annuler')}
        okButtonProps={{ disabled: !modalChoixTe?.victimeTrackingId, style: { background: G, borderColor: G } }}
        onOk={() => {
          const v = (victimes || []).find(x => x.victimeTrackingId === modalChoixTe.victimeTrackingId);
          const pseudoAff = {
            victimeTrackingId: v.victimeTrackingId,
            victimeNomPrenom: `${v.prenoms || ''} ${v.nom || ''}`.trim(),
            typeExpertise: undefined, expertTrackingId: undefined,
          };
          const nature = modalChoixTe.nature
            || (['BLESSE', 'DECEDE'].includes(v.typeVictime) ? 'MED' : 'MAT');
          setModalChoixTe(null);
          if (nature === 'MED') ouvrirModalMed(pseudoAff); else ouvrirModalMat(pseudoAff);
        }}>
        <Form layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item label={t('ongletExpertises.victimeConcernee')} required>
            <Select placeholder={t('ongletExpertises.choisirVictime')} value={modalChoixTe?.victimeTrackingId}
              onChange={(val) => setModalChoixTe(c => ({ ...c, victimeTrackingId: val }))}
              options={(victimes || []).map(v => ({
                value: v.victimeTrackingId,
                label: libelleVictime(v),
              }))} />
          </Form.Item>
          <Form.Item label={t('ongletExpertises.natureExpertise')}>
            <Select value={modalChoixTe?.nature
              || (['BLESSE', 'DECEDE'].includes((victimes || []).find(x => x.victimeTrackingId === modalChoixTe?.victimeTrackingId)?.typeVictime) ? 'MED' : 'MAT')}
              onChange={(val) => setModalChoixTe(c => ({ ...c, nature: val }))}
              options={[{ value: 'MED', label: t('ongletExpertises.natureMedDetail') }, { value: 'MAT', label: t('ongletExpertises.natureMatDetail') }]} />
          </Form.Item>
        </Form>
      </Modal>

      {/*  Modal affectation (CBModal = modal déplaçable)  */}
      <CBModal open={modalAff} title={t('ongletExpertises.affecterExpert')}
        onCancel={() => { setModalAff(false); setVictimeAff(null); }} footer={null} width={600} destroyOnClose>
        <Form form={formAff} layout="vertical" onFinish={affecterExpert} style={{ marginTop: 16 }}>
          <Form.Item name="victimeTrackingId" label={t('ongletExpertises.victime')} rules={[{ required: true }]}>
            <Select placeholder={t('ongletExpertises.selectionnerVictime')}
              onChange={(val) => {
                setVictimeAff((victimes || []).find(v => v.victimeTrackingId === val) || null);
                formAff.setFieldsValue({ expertTrackingId: undefined });
              }}
              options={(victimes || []).map(v => ({
                value: v.victimeTrackingId,
                label: libelleVictime(v),
              }))} />
          </Form.Item>

          {/* Type d'expertise déduit du dommage de la victime sélectionnée */}
          {victimeAff && (() => {
            const td = victimeAff.typeDommage;
            const msg = td === 'CORPOREL' ? t('ongletExpertises.aRealiser.CORPOREL')
              : td === 'MATERIEL' ? t('ongletExpertises.aRealiser.MATERIEL')
                : td === 'MIXTE' ? t('ongletExpertises.aRealiser.MIXTE')
                  : t('ongletExpertises.aRealiser.inconnu');
            return (
              <Alert
                type={td ? 'info' : 'warning'} showIcon
                icon={td === 'MATERIEL' ? <CarOutlined /> : <MedicineBoxOutlined />}
                style={{ marginBottom: 16, borderRadius: 6 }}
                message={<span><strong>{t('ongletExpertises.expertiseARealiser')}</strong> {msg}</span>}
              />
            );
          })()}

          <Form.Item name="typeExpertise" label={t('ongletExpertises.typeExpertise')} rules={[{ required: true }]}>
            <Select options={TYPE_EXPERTISE} />
          </Form.Item>
          <Form.Item name="expertTrackingId" label={t('ongletExpertises.expert')} rules={[{ required: true }]}>
            <Select showSearch
              filterOption={(input, opt) => (opt.label || '').toLowerCase().includes(input.toLowerCase())}
              placeholder={t('ongletExpertises.rechercherExpert')}
              notFoundContent={t('ongletExpertises.aucunExpert')}
              options={(() => {
                // Experts groupés par type, filtrés selon le dommage de la victime.
                const td = victimeAff?.typeDommage;
                const wantMed = !td || td === 'CORPOREL' || td === 'MIXTE';
                const wantAuto = !td || td === 'MATERIEL' || td === 'MIXTE';
                const mk = (e) => ({
                  value: e.expertTrackingId,
                  label: `${e.nomComplet}${e.specialite ? ' - ' + e.specialite : ''}`,
                });
                return [
                  wantMed && { label: t('ongletExpertises.expertsMedicaux'), options: (expertsMed || []).filter(e => e.actif).map(mk) },
                  wantAuto && { label: t('ongletExpertises.expertsAutomobiles'), options: (expertsAuto || []).filter(e => e.actif).map(mk) },
                ].filter(Boolean);
              })()} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="dateAffectation" label={t('ongletExpertises.dateAffectation')} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" defaultValue={dayjs()} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="dateLimiteRapport" label={t('ongletExpertises.dateLimiteRapport')}
                extra={
                  <span style={{ fontSize: 11, color: '#6b7280' }}>
                    <Trans
                      t={t}
                      i18nKey="ongletExpertises.delaiPrerempli"
                      values={{
                        valeur: delaiExpertise.valeur,
                        unite: delaiExpertise.unite === 'JOURS_CALENDAIRES' ? t('ongletExpertises.unites.JOURS_CALENDAIRES')
                      : delaiExpertise.unite === 'JOURS_OUVRES' ? t('ongletExpertises.unites.JOURS_OUVRES')
                        : delaiExpertise.unite === 'MOIS' ? t('ongletExpertises.unites.MOIS')
                          : delaiExpertise.unite === 'ANS' ? t('ongletExpertises.unites.ANS')
                            : delaiExpertise.unite,
                      }}
                      components={{ strong: <strong /> }}
                    />
                  </span>
                }>
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="observations" label={t('ongletExpertises.observations')}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => { setModalAff(false); setVictimeAff(null); }}>{t('ongletExpertises.annuler')}</Button>
            <Button type="primary" htmlType="submit" loading={saving}
              style={{ background: G, borderColor: G }}>
              {t('ongletExpertises.affecterEtGenerer')}
            </Button>
          </div>
        </Form>
      </CBModal>

      {/*  Modal Rapport Médical - Création / Édition 
          Un seul modal pour les deux cas. Le titre et le bouton s'adaptent
          selon modalRapportMed.expertiseExistante.
       */}
      <Modal
        open={!!modalRapportMed}
        title={
          <Space>
            <UserOutlined />
            {modalRapportMed?.expertiseExistante
              ? t('ongletExpertises.modifierMedTitre', { nom: modalRapportMed.affectation?.victimeNomPrenom })
              : t('ongletExpertises.nouveauMedTitre', { nom: modalRapportMed?.affectation?.victimeNomPrenom })}
            {modalRapportMed?.expertiseExistante && (
              <Tag color="orange" style={{ borderRadius: 20 }}>{t('ongletExpertises.modification')}</Tag>
            )}
          </Space>
        }
        onCancel={closeMed}
        footer={null}
        width={700}
        destroyOnClose
      >
        <Form form={formMed} layout="vertical" onFinish={enregistrerRapportMed} style={{ marginTop: 16 }}>
          {estTe && (
            <Form.Item name="nomExpertExterne" label={t('ongletExpertises.expertExterne')}>
              <Input placeholder={t('ongletExpertises.expertExterneMedPlaceholder')} maxLength={200} />
            </Form.Item>
          )}
          <FormRapportMedContenu pretium={PRETIUM} typeExpertise={TYPE_EXPERTISE} />

          <Divider style={{ fontSize: 12 }}>{t('ongletExpertises.rapportPdf')}</Divider>

          {/* Bandeau si un document GED est déjà attaché et qu'aucun nouveau fichier n'est sélectionné */}
          {modalRapportMed?.expertiseExistante?.ossanGedDocumentId && !fichierMed && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 12px', background: '#f0fdf4',
              border: '1px solid #bbf7d0', borderRadius: 8, marginBottom: 12,
            }}>
              <CheckCircleOutlined style={{ color: '#16a34a', fontSize: 18 }} />
              <span style={{ flex: 1, fontSize: 13, color: '#166534' }}>
                <Trans
                  t={t}
                  i18nKey="ongletExpertises.dejaRenseigne"
                  values={{ id: modalRapportMed.expertiseExistante.ossanGedDocumentId }}
                  components={{ strong: <strong /> }}
                />
              </span>
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => ouvrirVoirRapport(modalRapportMed.expertiseExistante, 'med')}
              >
                {t('ongletExpertises.previsualiser')}
              </Button>
              <Button size="small" type="link" onClick={() => setRemplacerMed(true)}>
                {t('ongletExpertises.remplacer')}
              </Button>
            </div>
          )}

          {/* Masqué tant qu'un rapport est archivé et qu'on n'a pas demandé à le
              remplacer : un champ visible demande à être rempli, et faisait
              douter le rédacteur de ce qui était déjà là. */}
          {(!modalRapportMed?.expertiseExistante?.ossanGedDocumentId || remplacerMed) && (
            <GedFileField
              value={fichierMed}
              onChange={setFichierMed}
              maxSizeMo={20}
              hint={
                modalRapportMed?.expertiseExistante?.ossanGedDocumentId
                  ? t('ongletExpertises.remplacera')
                  : <Trans t={t} i18nKey="ongletExpertises.archiveAvecTag" values={{ tag: 'RAPPORT_MEDICAL' }} components={{ strong: <strong /> }} />
              }
            />
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <Button onClick={closeMed} style={{ marginRight: 8 }}>{t('ongletExpertises.annuler')}</Button>
            <Button type="primary" htmlType="submit" loading={saving}
              icon={<SaveOutlined />} style={{ background: G, borderColor: G }}>
              {modalRapportMed?.expertiseExistante ? t('ongletExpertises.mettreAJour') : t('ongletExpertises.enregistrerRapport')}
            </Button>
          </div>
        </Form>
      </Modal>

      {/*  Modal Rapport Matériel - Création / Édition  */}
      <Modal
        open={!!modalRapportMat}
        title={
          <Space>
            <CarOutlined />
            {modalRapportMat?.expertiseExistante
              ? t('ongletExpertises.modifierMatTitre', { nom: modalRapportMat.affectation?.victimeNomPrenom })
              : t('ongletExpertises.nouveauMatTitre', { nom: modalRapportMat?.affectation?.victimeNomPrenom })}
            {modalRapportMat?.expertiseExistante && (
              <Tag color="orange" style={{ borderRadius: 20 }}>{t('ongletExpertises.modification')}</Tag>
            )}
          </Space>
        }
        onCancel={closeMat}
        footer={null}
        width={700}
        destroyOnClose
      >
        <Form form={formMat} layout="vertical" onFinish={enregistrerRapportMat} style={{ marginTop: 16 }}>
          {estTe && (
            <Form.Item name="nomExpertExterne" label={t('ongletExpertises.expertExterne')}>
              <Input placeholder={t('ongletExpertises.expertExterneMatPlaceholder')} maxLength={200} />
            </Form.Item>
          )}
          <Form.Item name="typeExpertise" label={t('ongletExpertises.typeExpertise')} rules={[{ required: true }]}>
            <Select options={TYPE_EXPERTISE} />
          </Form.Item>
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item name="dateDemande" label={t('ongletExpertises.dateDemande')} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabledDate={disabledFuture} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="dateRapport" label={t('ongletExpertises.dateRapport')}>
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabledDate={disabledFuture} />
              </Form.Item>
            </Col>
          </Row>
          <Divider style={{ fontSize: 12 }}>{t('ongletExpertises.vehicule')}</Divider>
          <Row gutter={8}>
            <Col span={12}><Form.Item name="marqueVehicule" label={t('ongletExpertises.marque')}>
              <SelectWithAdd categorie="MARQUE" valueKey="valeur"
                placeholder={t('ongletExpertises.selectionnerMarque')} addLabel={t('ongletExpertises.ajouterMarque')} style={{ width: '100%' }} />
            </Form.Item></Col>
            <Col span={12}><Form.Item name="modeleVehicule" label={t('ongletExpertises.modele')}><Input placeholder="Corolla, 406..." /></Form.Item></Col>
          </Row>
          <Row gutter={8}>
            <Col span={12}><Form.Item name="immatriculation" label={t('ongletExpertises.immatriculation')}><Input placeholder="TG-XXXX-XX" /></Form.Item></Col>
            <Col span={12}><Form.Item name="anneeVehicule" label={t('ongletExpertises.annee')}><CBInputNumber min={1970} max={2030} style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Form.Item name="natureDommages" label={t('ongletExpertises.natureDommages')}>
            <Input.TextArea rows={2} placeholder={t('ongletExpertises.natureDommagesPlaceholder')} />
          </Form.Item>
          <Form.Item name="estVei" label={t('ongletExpertises.veiLong')} valuePropName="checked">
            <Switch />
          </Form.Item>
          <Divider style={{ fontSize: 12 }}>{t('ongletExpertises.valeursMontants')}</Divider>
          <Row gutter={8}>
            <Col span={12}><Form.Item name="valeurVehiculeNeuf" label={t('ongletExpertises.valeurNeufFcfa')}><CBInputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
            <Col span={12}><Form.Item name="valeurVenal" label={t('ongletExpertises.valeurVenaleFcfa')}><CBInputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
          </Row>
          <Row gutter={8}>
            <Col span={12}><Form.Item name="montantDevis" label={t('ongletExpertises.montantDevisFcfa')}><CBInputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
            <Col span={12}><Form.Item name="montantDitExpert" label={t('ongletExpertises.montantDitExpertFcfa')}><CBInputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
          </Row>
          <Row gutter={8}>
            <Col span={12}><Form.Item name="valeurReparable" label={t('ongletExpertises.valeurReparableFcfa')}><CBInputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
            <Col span={12}><Form.Item name="honoraires" label={t('ongletExpertises.honorairesFcfa')}><CBInputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
          </Row>
          <Form.Item name="observations" label={t('ongletExpertises.observations')}><Input.TextArea rows={2} /></Form.Item>

          <Divider style={{ fontSize: 12 }}>{t('ongletExpertises.rapportPdf')}</Divider>

          {/* Bandeau GED existant */}
          {modalRapportMat?.expertiseExistante?.ossanGedDocumentId && !fichierMat && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 12px', background: '#f0fdf4',
              border: '1px solid #bbf7d0', borderRadius: 8, marginBottom: 12,
            }}>
              <CheckCircleOutlined style={{ color: '#16a34a', fontSize: 18 }} />
              <span style={{ flex: 1, fontSize: 13, color: '#166534' }}>
                <Trans
                  t={t}
                  i18nKey="ongletExpertises.dejaRenseigne"
                  values={{ id: modalRapportMat.expertiseExistante.ossanGedDocumentId }}
                  components={{ strong: <strong /> }}
                />
              </span>
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => ouvrirVoirRapport(modalRapportMat.expertiseExistante, 'mat')}
              >
                {t('ongletExpertises.previsualiser')}
              </Button>
              <Button size="small" type="link" onClick={() => setRemplacerMat(true)}>
                {t('ongletExpertises.remplacer')}
              </Button>
            </div>
          )}

          {/* Masqué tant qu'un rapport est archivé et qu'on n'a pas demandé à le
              remplacer : un champ visible demande à être rempli, et faisait
              douter le rédacteur de ce qui était déjà là. */}
          {(!modalRapportMat?.expertiseExistante?.ossanGedDocumentId || remplacerMat) && (
            <GedFileField
              value={fichierMat}
              onChange={setFichierMat}
              maxSizeMo={20}
              hint={
                modalRapportMat?.expertiseExistante?.ossanGedDocumentId
                  ? t('ongletExpertises.remplacera')
                  : <Trans t={t} i18nKey="ongletExpertises.archiveAvecTag" values={{ tag: 'RAPPORT_MATERIEL' }} components={{ strong: <strong /> }} />
              }
            />
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <Button onClick={closeMat} style={{ marginRight: 8 }}>{t('ongletExpertises.annuler')}</Button>
            <Button type="primary" htmlType="submit" loading={saving}
              icon={<SaveOutlined />} style={{ background: G, borderColor: G }}>
              {modalRapportMat?.expertiseExistante ? t('ongletExpertises.mettreAJour') : t('ongletExpertises.enregistrerRapport')}
            </Button>
          </div>
        </Form>
      </Modal>

      {/*  Modal Visualisation complète d'un rapport 
          Affiche le récapitulatif des métadonnées (colonne gauche)
          et le PDF depuis la GED (colonne droite via iframe blob).
       */}
      <Modal
        open={!!modalVoirRapport}
        title={
          <Space>
            <FileTextOutlined style={{ color: '#3b82f6' }} />
            {modalVoirRapport?.type === 'med'
              ? t('ongletExpertises.voirMedTitre', { nom: modalVoirRapport?.expertise?.victimeNomPrenom || '' })
              : t('ongletExpertises.voirMatTitre', { nom: modalVoirRapport?.expertise?.victimeNomPrenom || '' })}
          </Space>
        }
        onCancel={fermerVoirRapport}
        footer={
          <Space>
            <Button onClick={fermerVoirRapport}>{t('ongletExpertises.fermer')}</Button>
            {blobUrl && (
              <Button
                type="primary"
                icon={<FilePdfOutlined />}
                onClick={() => window.open(blobUrl, '_blank')}
                style={{ background: G, borderColor: G }}
              >
                {t('ongletExpertises.pleinEcran')}
              </Button>
            )}
          </Space>
        }
        width={980}
        destroyOnClose
      >
        {modalVoirRapport && (
          <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 20, marginTop: 8 }}>
            {/* Colonne métadonnées */}
            <div>
              {modalVoirRapport.type === 'med'
                ? <RecapMedical expertise={modalVoirRapport.expertise} />
                : <RecapMateriel expertise={modalVoirRapport.expertise} />}
            </div>

            {/* Colonne PDF */}
            <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
              <div style={{
                background: '#1e293b', color: '#fff',
                padding: '6px 12px', fontSize: 12,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <EyeOutlined /> {t('ongletExpertises.rapportPdf')}
              </div>
              {loadingBlob ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 500 }}>
                  <Spin size="large" tip={t('ongletExpertises.chargementDocument')} />
                </div>
              ) : blobUrl ? (
                <iframe
                  src={blobUrl}
                  title={t('ongletExpertises.rapportPdf')}
                  style={{ width: '100%', height: 520, border: 'none' }}
                />
              ) : (
                <div style={{
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  height: 500, color: '#94a3b8', gap: 8,
                }}>
                  <FilePdfOutlined style={{ fontSize: 36 }} />
                  <span>{t('ongletExpertises.aucunPdf')}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/*  Modal Note de Mission  */}
      <Modal
        open={!!modalNoteMission}
        title={<span><FileTextOutlined /> {t('ongletExpertises.noteMissionTitre', { nom: modalNoteMission?.victimeNomPrenom })}</span>}
        onCancel={() => setModalNoteMission(null)}
        width={860}
        footer={
          <Space>
            <Button onClick={() => setModalNoteMission(null)}>{t('ongletExpertises.fermer')}</Button>

            <Button type="primary" icon={<PrinterOutlined />}
              style={{ background: G, borderColor: G }}
              onClick={() => imprimerNoteMission(modalNoteMission)}>
              {t('ongletExpertises.imprimer')}
            </Button>
          </Space>
        }
      >
        {modalNoteMission && (
          <div ref={noteMissionRef} style={{
            border: '1px solid #e2e8f0', borderRadius: 8,
            maxHeight: 560, overflowY: 'auto', background: '#fff',
            /* Les marges du document vivent dans @page, donc a l'impression
               seulement. L'apercu a l'ecran les reproduit ici, sinon le texte se
               collerait au bord du cadre. */
            padding: '18px 20px',
          }}>
            <NoteMissionExpert sinistre={sinistre} affectation={modalNoteMission} />
          </div>
        )}
      </Modal>

      {/*  Modal Note Victime  */}
      <Modal
        open={!!modalNoteVictime}
        title={<span><FileTextOutlined /> {t('ongletExpertises.noteVictimeTitre', { nom: modalNoteVictime?.victimeNomPrenom })}</span>}
        onCancel={() => setModalNoteVictime(null)}
        width={860}
        footer={
          <Space>
            <Button onClick={() => setModalNoteVictime(null)}>{t('ongletExpertises.fermer')}</Button>

            <Button type="primary" icon={<PrinterOutlined />}
              style={{ background: '#2563eb', borderColor: '#2563eb' }}
              onClick={() => imprimerNoteVictime(modalNoteVictime)}>
              {t('ongletExpertises.imprimer')}
            </Button>
          </Space>
        }
      >
        {modalNoteVictime && (
          <div ref={noteVictimeRef} style={{
            border: '1px solid #e2e8f0', borderRadius: 8,
            maxHeight: 560, overflowY: 'auto', background: '#fff',
            /* Les marges du document vivent dans @page, donc a l'impression
               seulement. L'apercu a l'ecran les reproduit ici, sinon le texte se
               collerait au bord du cadre. */
            padding: '18px 20px',
          }}>
            <NoteVictime sinistre={sinistre} affectation={modalNoteVictime} immatriculation={immatDe(modalNoteVictime)} langue={langueDe(modalNoteVictime)} />
          </div>
        )}
      </Modal>

      {/*  Modal Aperçu courrier  */}
      <Modal
        open={!!modalCourrier}
        title={<span><PrinterOutlined /> {modalCourrier?.titre}</span>}
        onCancel={() => setModalCourrier(null)}
        width={780}
        footer={
          <Space>
            <Button onClick={() => setModalCourrier(null)}>{t('ongletExpertises.fermer')}</Button>
            <Button type="primary" icon={<PrinterOutlined />} onClick={doPrint}
              style={{ background: G, borderColor: G }}>
              {t('ongletExpertises.imprimer')}
            </Button>
          </Space>
        }
      >
        {modalCourrier && (
          <div style={{
            border: '1px solid #e2e8f0', borderRadius: 8, padding: 16,
            maxHeight: 500, overflowY: 'auto', background: '#fff',
          }}
            dangerouslySetInnerHTML={{ __html: modalCourrier.html }}
          />
        )}
      </Modal>
    </div>
  );
}

//  Sous-composant : champs du formulaire rapport médical 
function FormRapportMedContenu({ pretium, typeExpertise }) {
  const { t } = useTranslation('dossierSinistre');
  return (
    <>
      <Form.Item name="typeExpertise" label={t('ongletExpertises.typeExpertise')} rules={[{ required: true }]}>
        <Select options={typeExpertise} />
      </Form.Item>
      <Row gutter={8}>
        <Col span={12}>
          <Form.Item name="dateDemande" label={t('ongletExpertises.dateDemande')} rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabledDate={disabledFuture} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="dateRapport" label={t('ongletExpertises.dateRapport')}>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabledDate={disabledFuture} />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="dateConsolidation" label={t('ongletExpertises.dateConsolidation')}>
        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabledDate={disabledFuture} />
      </Form.Item>
      <Divider style={{ fontSize: 12 }}>{t('ongletExpertises.evaluationCima')}</Divider>
      <Row gutter={8}>
        <Col span={12}>
          <Form.Item name="tauxIpp" label={t('ongletExpertises.tauxIppPct')}>
            <CBInputNumber min={0} max={100} style={{ width: '100%' }} suffix="%" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="dureeIttJours" label={t('ongletExpertises.ittJours')}>
            <CBInputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
      {/* <Form.Item name="dureeItpJours" label="ITP (jours)">
        <CBInputNumber min={0} style={{ width: '100%' }} />
      </Form.Item> */}
      <Row gutter={8}>
        <Col span={12}>
          <Form.Item name="pretiumDoloris" label={t('ongletExpertises.pretiumDoloris')}>
            <Select options={pretium.map(p => ({ value: p, label: libellePretium(p) }))} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="prejudiceEsthetique" label={t('ongletExpertises.prejudiceEsthetique')}>
            <Select options={pretium.map(p => ({ value: p, label: libellePretium(p) }))} />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="necessiteTiercePersonne" label={t('ongletExpertises.tiercePersonne')} valuePropName="checked">
        <Switch />
      </Form.Item>
      <Row gutter={8}>
        <Col span={12}>
          <Form.Item name="honoraires" label={t('ongletExpertises.honorairesFcfa')}>
            <CBInputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="honorairesContreExpertise" label={t('ongletExpertises.honorairesContreExp')}>
            <CBInputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
}

//  Sous-composants récapitulatifs 
function RecapMedical({ expertise }) {
  const { t } = useTranslation('dossierSinistre');
  const fmt = v => (v != null ? v : '-');
  const fmtM = v => (v != null ? `${Number(v).toLocaleString(localeIntl())} FCFA` : '-');
  return (
    <RecapCard titre={t('ongletExpertises.recap.donneesMedicales')} rows={[
      { label: t('ongletExpertises.recap.typeExpertise'), value: expertise.typeExpertise },
      { label: t('ongletExpertises.recap.expert'), value: expertise.expertNomComplet || '-' },
      { label: t('ongletExpertises.recap.dateDemande'), value: fmt(expertise.dateDemande) },
      { label: t('ongletExpertises.recap.dateRapport'), value: fmt(expertise.dateRapport) },
      { label: t('ongletExpertises.recap.consolidation'), value: fmt(expertise.dateConsolidation) },
      { label: t('ongletExpertises.recap.tauxIpp'), value: expertise.tauxIpp != null ? `${expertise.tauxIpp}%` : '-' },
      { label: t('ongletExpertises.recap.itt'), value: expertise.dureeIttJours != null ? t('ongletExpertises.recap.jours', { n: expertise.dureeIttJours }) : '-' },
      // { label: 'ITP', value: expertise.dureeItpJours != null ? `${expertise.dureeItpJours} j` : '-' },
      { label: t('ongletExpertises.recap.pretiumDoloris'), value: libellePretium(expertise.pretiumDoloris) || '-' },
      { label: t('ongletExpertises.recap.prejEsthetique'), value: libellePretium(expertise.prejudiceEsthetique) || '-' },
      { label: t('ongletExpertises.recap.tiercePersonne'), value: expertise.necessiteTiercePersonne ? t('ongletExpertises.recap.oui') : t('ongletExpertises.recap.non') },
      { label: t('ongletExpertises.recap.honoraires'), value: fmtM(expertise.honoraires) },
      { label: t('ongletExpertises.recap.honorairesCe'), value: fmtM(expertise.honorairesContreExpertise) },
    ]} />
  );
}

function RecapMateriel({ expertise }) {
  const { t } = useTranslation('dossierSinistre');
  const fmt = v => (v != null ? v : '-');
  const fmtM = v => (v != null ? `${Number(v).toLocaleString(localeIntl())} FCFA` : '-');
  return (
    <RecapCard titre={t('ongletExpertises.recap.donneesMaterielles')} rows={[
      { label: t('ongletExpertises.recap.typeExpertise'), value: expertise.typeExpertise },
      { label: t('ongletExpertises.recap.expert'), value: expertise.expertNomComplet || '-' },
      { label: t('ongletExpertises.recap.dateDemande'), value: fmt(expertise.dateDemande) },
      { label: t('ongletExpertises.recap.dateRapport'), value: fmt(expertise.dateRapport) },
      { label: t('ongletExpertises.recap.vehicule'), value: [expertise.marqueVehicule, expertise.modeleVehicule].filter(Boolean).join(' ') || '-' },
      { label: t('ongletExpertises.recap.immatriculation'), value: fmt(expertise.immatriculation) },
      { label: t('ongletExpertises.recap.annee'), value: fmt(expertise.anneeVehicule) },
      { label: t('ongletExpertises.recap.vei'), value: expertise.estVei ? <><WarningOutlined style={{ marginRight: 4, color: '#f59e0b' }} />{t('ongletExpertises.recap.oui')}</> : t('ongletExpertises.recap.non') },
      { label: t('ongletExpertises.recap.valeurVenale'), value: fmtM(expertise.valeurVenal) },
      { label: t('ongletExpertises.recap.valeurNeuf'), value: fmtM(expertise.valeurVehiculeNeuf) },
      { label: t('ongletExpertises.recap.montantDevis'), value: fmtM(expertise.montantDevis) },
      { label: t('ongletExpertises.recap.ditExpert'), value: fmtM(expertise.montantDitExpert) },
      { label: t('ongletExpertises.recap.reparable'), value: fmtM(expertise.valeurReparable) },
      { label: t('ongletExpertises.recap.honoraires'), value: fmtM(expertise.honoraires) },
    ]} />
  );
}

function RecapCard({ titre, rows }) {
  return (
    <div style={{ background: '#f8fafc', borderRadius: 10, padding: 14, border: '1px solid #e5e7eb' }}>
      <div style={{
        fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '0.06em', color: '#374151', marginBottom: 12,
      }}>
        {titre}
      </div>
      {rows.map(({ label, value }) => (
        <div key={label} style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', padding: '5px 0',
          borderBottom: '1px solid #f1f5f9', gap: 8,
        }}>
          <span style={{ fontSize: 12, color: '#6b7280', flexShrink: 0 }}>{label}</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#111827', textAlign: 'right' }}>{value}</span>
        </div>
      ))}
    </div>
  );
}